import type { CashSummaryDto } from '@hbs-us/contracts';
import { useTranslation } from '@hbs-us/ui';
import type { ReportFormatters } from '../useReportFormatters.js';

/**
 * Cost breakdown card (legacy ReportsPage 161–191): the five cost buckets —
 * Materials · Subcontractors · Labor · Monthly Bills · Fleet — inside one card,
 * laid out on an auto-fit CSS grid. Numerals in IBM Plex Mono; every label/sub
 * is t()-keyed (reports namespace).
 */

interface CostItem {
  labelKey: string;
  subKey: string;
  value: number | null;
}

export function CostBreakdown({ cash, fmt }: { cash: CashSummaryDto; fmt: ReportFormatters }) {
  const { t } = useTranslation();

  const items: CostItem[] = [
    {
      labelKey: 'reports:costs.materials',
      subKey: 'reports:costs.materialsSub',
      value: Number(cash.materialsCost || 0),
    },
    {
      labelKey: 'reports:costs.subcontractors',
      subKey: 'reports:costs.subcontractorsSub',
      value: Number(cash.subcontractorCost || 0),
    },
    {
      labelKey: 'reports:costs.labor',
      subKey: 'reports:costs.laborSub',
      value: (cash.laborCost as number | null) === null ? null : Number(cash.laborCost || 0),
    },
    {
      labelKey: 'reports:costs.monthlyBills',
      subKey: 'reports:costs.monthlyBillsSub',
      value: Number(cash.monthlyBillsCost || 0),
    },
    {
      labelKey: 'reports:costs.fleet',
      subKey: 'reports:costs.fleetSub',
      value: Number(cash.fleetCost || 0),
    },
  ];

  return (
    <section className="mb-4 rounded-md border border-border bg-card p-6">
      <h2 className="mb-4 font-semibold text-foreground text-xl">{t('reports:costs.heading')}</h2>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4">
        {items.map((item) => (
          <div key={item.labelKey} className="rounded-md border border-border bg-background p-4">
            <div className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
              {t(item.labelKey)}
            </div>
            <div className="mt-2 font-mono font-semibold text-foreground text-lg">
              {fmt.money(item.value)}
            </div>
            <div className="mt-1 text-muted-foreground text-xs">{t(item.subKey)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
